/**
 * Content API（統合面 `/api/v1/content`）の zod 契約。
 * `docs/CMS_GAP_AND_API.md` §3.3 / §4 / §6 が唯一の正。
 *
 * **配信 API（`/api/v1/delivery`）は `contracts/delivery.ts` に分離した。**
 * 統合面の共通部品（ID・日時・ページング・冪等キー）はこのファイルが唯一の出典で、
 * delivery 側はここから import する。
 *
 * この面の設計上の核心を型で表現している。
 *
 * 1. **本文が API を通らない。** リビジョンの取得は署名付き URL を返すだけで、
 *    保存は presign → PUT → commit の 3 段。`inline` は 512KB 上限の補助経路。
 *    レスポンス型に `docJson` / `bodyHtml` を持つものが 1 つも無いのはそのため。
 * 2. **一覧の応答に `searchScope` を必ず載せる。** 本文の全文検索は
 *    「本文を CP DB に置かない」設計の代償として意図的に無い。
 *    画面の注記だけだと API 利用者に伝わらないので、レスポンスで伝える。
 * 3. **統合面から押せる操作を型で絞る。** 承認と配信開始の契約はここに無い
 *    （承認は `contracts/content-api.ts` に置かず、配信開始は `contracts/delivery.ts` にも無い）。
 */
import { z } from 'zod';
import {
  CONTENT_DOC_FORMAT,
  CONTENT_IMPORT_STATUS,
  CONTENT_KIND,
  CONTENT_ORIGIN,
  CONTENT_RETENTION_CLASS,
  CONTENT_REVIEW_STATE,
  CONTENT_SCHEDULE_ACTION,
  CONTENT_SCHEDULE_STATE,
  CONTENT_TAG_KIND,
  CONTENT_UPLOAD_STATE,
} from '../enums.js';
import { CONTENT_ISSUE_CODES, CONTENT_ISSUE_SEVERITIES, blocksDocumentSchema } from '../content.js';

/* ============================================================ 共通の部品 */

/** 統合面の API バージョン。全レスポンスに `MailSV-Api-Version: v1` を付ける。 */
export const INTEGRATION_API_VERSION = 'v1';

/** 統合面のパス接頭辞。**JWT を受け付けず CORS も返さない面**。 */
export const INTEGRATION_PATH_PREFIXES = ['/api/v1/content', '/api/v1/delivery'] as const;

/**
 * 統合面の ID（ULID を想定）。`contracts/delivery.ts` と `contracts/signup.ts`
 * からも使うので named export にしてある。**定義を写経しないこと**
 * （長さ上限だけ違う 2 つの ID スキーマができると、境界で必ず片方が緩む）。
 */
export const integrationIdSchema = z.string().min(1).max(64);
/** ISO 8601（オフセット必須）。ローカル時刻の解釈揺れを構造的に排除する。 */
export const integrationDateTimeSchema = z.string().datetime({ offset: true });

const id = integrationIdSchema;
const isoDateTime = integrationDateTimeSchema;

/**
 * 本文のハッシュ。**`sha256:` 接頭辞つきの小文字 hex に固定する**。
 * 接頭辞を任意にすると「どちらの形で送るか」で必ず食い違う。
 */
export const contentHashSchema = z
  .string()
  .regex(/^sha256:[0-9a-f]{64}$/, 'contentHash は sha256:<64 桁の小文字 hex> の形式です');

export const externalRefSchema = z.string().min(1).max(200);

/** カーソルページング。**総件数は返さない**（既存規約）。 */
export const cursorQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().max(500).optional(),
});

export function pageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ items: z.array(item), nextCursor: z.string().nullable() });
}

/**
 * 署名付き URL の記述子。**発行するだけで DB に保存しない**。
 * 監査に書くのは `object_key` と `content_hash` だけ。
 */
export const presignedUrlSchema = z
  .object({
    url: z.string().url(),
    method: z.enum(['GET', 'PUT']),
    headers: z.record(z.string()).default({}),
    expiresAt: isoDateTime,
  })
  .strict();

/** 本文取得用の署名付き URL の有効期限（秒）。エージェント面の 1 時間より短い。 */
export const CONTENT_BODY_URL_TTL_SEC = 300;

/** inline 保存経路の上限（`CONTENT_INLINE_BODY_MAX_BYTES` の既定値）。 */
export const CONTENT_INLINE_BODY_MAX_BYTES_DEFAULT = 512 * 1024;

/** 共有プレビューリンクの最大有効日数（`CONTENT_SHARE_MAX_DAYS` の既定値）。 */
export const CONTENT_SHARE_MAX_DAYS_DEFAULT = 14;

/** 冪等キーのヘッダ名。**BullMQ の jobId とは別物**で `:` を含んでよい。 */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key';
export const IDEMPOTENCY_REPLAYED_HEADER = 'Idempotency-Replayed';
export const IDEMPOTENCY_KEY_TTL_SEC = 24 * 60 * 60;

export const idempotencyKeySchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^[\x20-\x7e]+$/, 'Idempotency-Key は ASCII 印字可能文字だけで指定してください');

/** `Idempotency-Key` を**必須**にするエンドポイント（§6.3）。副作用が重い 4 本。 */
export const IDEMPOTENCY_REQUIRED_ROUTES = [
  'POST /api/v1/content/documents',
  'POST /api/v1/content/documents/:id/revisions',
  'POST /api/v1/delivery/campaigns',
  'POST /api/v1/delivery/campaigns/:id/schedule',
] as const;

/* ================================================================ ping */

/**
 * `GET /api/v1/content/ping`。
 * トークンが通ることと権限を、副作用なしで確認できる入口が無いと統合の初期構築で必ず詰まる。
 */
export const integrationPingSchema = z
  .object({
    organizationId: id,
    tokenPrefix: z.string(),
    scopes: z.array(z.string()),
    apiVersion: z.literal(INTEGRATION_API_VERSION),
    serverTime: isoDateTime,
  })
  .strict();

/* ============================================================ ドキュメント */

export const contentDocumentSchema = z
  .object({
    id,
    kind: z.enum(CONTENT_KIND),
    name: z.string(),
    subject: z.string().nullable(),
    preheader: z.string().nullable(),
    tags: z.array(z.string()),
    campaignId: id.nullable(),
    currentRevision: z.number().int().nonnegative().nullable(),
    currentRevisionId: id.nullable(),
    reviewState: z.enum(CONTENT_REVIEW_STATE).nullable(),
    origin: z.enum(CONTENT_ORIGIN),
    externalRef: z.string().nullable(),
    archivedAt: isoDateTime.nullable(),
    lastActivityAt: isoDateTime,
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
    /** 楽観ロック用。`If-Match` で送り返す。 */
    revision: z.number().int().nonnegative(),
    /**
     * 編集ロック（誰が編集中か）。**実装は一覧でこの 2 つを返しているのに
     * 契約に書かれておらず、`.strict()` で弾かれていた**（統合面の応答を
     * 契約で検証するテストを足して発覚）。任意にしてあるのは、
     * ロックを扱わない経路（作成直後の応答など）が返さないため。
     */
    lockedByOrgUserId: id.nullable().optional(),
    lockExpiresAt: isoDateTime.nullable().optional(),
  })
  .strict();

export type ContentDocumentView = z.infer<typeof contentDocumentSchema>;

export const createContentDocumentSchema = z
  .object({
    kind: z.enum(CONTENT_KIND).default('campaign'),
    name: z.string().min(1).max(200),
    subject: z.string().max(500).optional(),
    preheader: z.string().max(500).optional(),
    tags: z.array(z.string().min(1).max(60)).max(30).optional(),
    campaignId: id.optional(),
    /**
     * 外部システム側の識別子。**部分 UNIQUE(organization_id, external_ref)** が張られ、
     * 重複した POST は 409 ではなく既存行を 200 で返す（何度投げても 1 個になる）。
     */
    externalRef: externalRefSchema.optional(),
  })
  .strict();

export const updateContentDocumentSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    subject: z.string().max(500).nullable().optional(),
    preheader: z.string().max(500).nullable().optional(),
    tags: z.array(z.string().min(1).max(60)).max(30).optional(),
    externalRef: externalRefSchema.nullable().optional(),
  })
  .strict();

export const duplicateContentDocumentSchema = z
  .object({
    name: z.string().min(1).max(200),
    /**
     * 複製先に付ける外部 ID。**複製元のものは引き継がない。**
     * `external_ref` は組織内で一意に使われる前提の紐づけキーなので、
     * 黙って同じ値を 2 行に入れると外部システム側の突合が壊れる。
     */
    externalRef: externalRefSchema.optional(),
  })
  .strict();

/** `GET /api/v1/content/documents` のクエリ（§4.4）。 */
export const listContentDocumentsQuerySchema = cursorQuerySchema.extend({
  /** `name` と `subject` の部分一致のみ。**本文は検索できない**。 */
  q: z.string().max(200).optional(),
  kind: z.enum(CONTENT_KIND).optional(),
  /** 複数指定は AND。 */
  tag: z.union([z.string(), z.array(z.string())]).optional(),
  reviewState: z.enum(CONTENT_REVIEW_STATE).optional(),
  updatedBy: id.optional(),
  updatedFrom: isoDateTime.optional(),
  updatedTo: isoDateTime.optional(),
  campaignId: id.optional(),
  archived: z.coerce.boolean().default(false),
  origin: z.enum(CONTENT_ORIGIN).optional(),
  externalRef: externalRefSchema.optional(),
  sort: z.enum(['lastActivity', 'name', 'createdAt']).default('lastActivity'),
});

/**
 * 検索対象の宣言。**必ず返す。**
 * 本文の全文検索が無いことを、統合を書く人が最初の 1 回で気づけるようにするため。
 */
export const CONTENT_SEARCH_SCOPE = ['name', 'subject'] as const;

export const listContentDocumentsResponseSchema = pageSchema(contentDocumentSchema).extend({
  searchScope: z.array(z.enum(CONTENT_SEARCH_SCOPE)),
});

/* ============================================================ リビジョン */

export const contentRevisionSchema = z
  .object({
    revisionId: id,
    revision: z.number().int().positive(),
    docFormat: z.enum(CONTENT_DOC_FORMAT),
    contentHash: contentHashSchema,
    byteSize: z.number().int().nonnegative(),
    uploadState: z.enum(CONTENT_UPLOAD_STATE),
    reviewState: z.enum(CONTENT_REVIEW_STATE),
    retentionClass: z.enum(CONTENT_RETENTION_CLASS),
    frozenAt: isoDateTime.nullable(),
    createdBy: id.nullable(),
    createdAt: isoDateTime,
  })
  .strict();

/**
 * `POST /documents/:id/revisions`（2 段階アップロードの ①）。
 *
 * **本文を送らない。** 送るのはハッシュとサイズの申告だけで、
 * 実体はレスポンスの presigned URL へ直接 PUT する（CP を通らない）。
 */
export const createRevisionSchema = z
  .object({
    docFormat: z.enum(CONTENT_DOC_FORMAT).default('blocks-v1'),
    contentHash: contentHashSchema,
    byteSize: z.number().int().positive().max(50 * 1024 * 1024),
    /** 直前の版。指定して食い違えば `409 REVISION_CONFLICT`（後勝ちで相手を消さない）。 */
    baseRevision: z.number().int().positive().optional(),
  })
  .strict();

export const createRevisionResponseSchema = z
  .object({
    revisionId: id,
    revision: z.number().int().positive(),
    state: z.literal('pending_upload'),
    upload: presignedUrlSchema,
  })
  .strict();

/**
 * `POST /documents/:id/revisions?inline=1`（補助経路）。
 *
 * SDK の無い相手（社内バッチ・ノーコードツール）向け。
 * **新しい保存経路ではない**。ハンドラは portal と同じ `saveRevision()` を呼ぶだけで、
 * S3 へ PUT して `object_key` と `content_hash` を書く挙動は 1 行も変わらない。
 * このルートだけリクエストボディのログを無効化する（ログに本文が出たら制約違反）。
 */
export const createRevisionInlineSchema = z
  .object({
    docFormat: z.literal('blocks-v1').default('blocks-v1'),
    doc: blocksDocumentSchema,
    baseRevision: z.number().int().positive().optional(),
  })
  .strict();

export const commitRevisionResponseSchema = z
  .object({
    revisionId: id,
    revision: z.number().int().positive(),
    state: z.enum(['draft', 'failed']),
    buildJobId: z.string().nullable(),
  })
  .strict();

/**
 * `GET /documents/:id/revisions/:rev/body`。
 * **API は本文を中継しない。** 署名付き URL を返して終わり。
 */
export const revisionBodyResponseSchema = z
  .object({
    revisionId: id,
    revision: z.number().int().positive(),
    docFormat: z.enum(CONTENT_DOC_FORMAT),
    contentHash: contentHashSchema,
    byteSize: z.number().int().nonnegative(),
    download: presignedUrlSchema,
  })
  .strict();

export const contentLintEntrySchema = z
  .object({
    code: z.enum(CONTENT_ISSUE_CODES),
    severity: z.enum(CONTENT_ISSUE_SEVERITIES),
    count: z.number().int().positive(),
  })
  .strict();

/**
 * `GET /documents/:id/revisions/:rev/artifact`。
 * 返すのは**成果物のメタと署名付き URL だけ**。HTML の中身は返さない。
 */
export const revisionArtifactResponseSchema = z
  .object({
    html: z.object({ url: z.string().url(), sha256: z.string(), bytes: z.number().int() }).strict(),
    text: z.object({ url: z.string().url(), length: z.number().int() }).strict(),
    mergeKeys: z.array(z.string()),
    /** eTLD+1 に丸めたリンク先ドメイン。生 URL は返さない。 */
    linkDomains: z.array(z.string()),
    hasLegalFooter: z.boolean(),
    imageOnly: z.boolean(),
    lint: z.array(contentLintEntrySchema),
    rendererVersion: z.string(),
    builtAt: isoDateTime,
  })
  .strict();

/** ビルド未完了のときの 202 応答。 */
export const revisionArtifactPendingSchema = z
  .object({ state: z.literal('building'), buildJobId: z.string().nullable() })
  .strict();

/**
 * `GET /documents/:id/revisions/:a/diff/:b`。
 *
 * **このエンドポイントだけは本文（テキストの実文）がレスポンスに出る。**
 * CD §3.3 のプレビューと同じ「HTTP レスポンスには出るが DB にもログにも残らない」区分。
 * 統合面に置くのは、外部 CMS が「前回から何が変わったか」を判断できないと
 * 承認フローを外に持ち出せないため。監査には**メタのみ**記録する。
 */
export const revisionDiffEntrySchema = z
  .object({
    op: z.enum(['added', 'removed', 'changed', 'moved']),
    path: z.string(),
    blockKind: z.string(),
    before: z.string().nullable(),
    after: z.string().nullable(),
  })
  .strict();

export const revisionDiffResponseSchema = z
  .object({
    from: z.number().int().positive(),
    to: z.number().int().positive(),
    entries: z.array(revisionDiffEntrySchema),
    mergeKeysAdded: z.array(z.string()),
    mergeKeysRemoved: z.array(z.string()),
    linkDomainsAdded: z.array(z.string()),
    linkDomainsRemoved: z.array(z.string()),
  })
  .strict();

/* ================================================================ タグ */

export const contentTagSchema = z
  .object({
    id,
    slug: z.string(),
    label: z.string(),
    color: z.string(),
    description: z.string().nullable(),
    kind: z.enum(CONTENT_TAG_KIND),
    usageCount: z.number().int().nonnegative(),
    archivedAt: isoDateTime.nullable(),
  })
  .strict();

export const createContentTagSchema = z
  .object({
    slug: z
      .string()
      .min(1)
      .max(60)
      .regex(/^[a-z0-9][a-z0-9_-]*$/, 'タグの slug は英小文字・数字・`_` `-` で指定してください'),
    label: z.string().min(1).max(60),
    color: z.string().min(1).max(30).default('slate'),
    description: z.string().max(200).optional(),
    kind: z.enum(CONTENT_TAG_KIND).default('both'),
  })
  .strict();

export const mergeContentTagSchema = z.object({ intoTagId: id }).strict();

/* ============================================================== アセット */

export const contentAssetVariantSchema = z
  .object({
    kind: z.string(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    format: z.string(),
    publicUrl: z.string().url(),
    bytes: z.number().int().nonnegative(),
  })
  .strict();

export const contentAssetSchema = z
  .object({
    id,
    fileName: z.string(),
    title: z.string().nullable(),
    altText: z.string(),
    contentType: z.string(),
    bytes: z.number().int().nonnegative(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    sha256: z.string(),
    folderId: id.nullable(),
    tags: z.array(z.string()),
    /** true のときだけ `stableUrl` を持つ。差し替えで既配信メールの見た目が変わる。 */
    mutable: z.boolean(),
    publicUrl: z.string().url(),
    stableUrl: z.string().url().nullable(),
    externalRef: z.string().nullable(),
    variants: z.array(contentAssetVariantSchema),
    lastUsedAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
  })
  .strict();

export const createContentAssetSchema = z
  .object({
    fileName: z.string().min(1).max(255),
    contentType: z.string().min(1).max(120),
    byteSize: z.number().int().positive(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    folderId: id.optional(),
    altText: z.string().max(300).optional(),
    title: z.string().max(200).optional(),
    tags: z.array(z.string().min(1).max(60)).max(30).optional(),
    mutable: z.boolean().default(false),
    externalRef: externalRefSchema.optional(),
  })
  .strict();

export const createContentAssetResponseSchema = z
  .object({ assetId: id, upload: presignedUrlSchema })
  .strict();

export const updateContentAssetSchema = z
  .object({
    altText: z.string().max(300).optional(),
    title: z.string().max(200).nullable().optional(),
    tags: z.array(z.string().min(1).max(60)).max(30).optional(),
    folderId: id.nullable().optional(),
    externalRef: externalRefSchema.nullable().optional(),
  })
  .strict();

/**
 * 差し替えの応答。**警告文を必ず含める**。
 * 「送信済みのメールで表示される画像も差し替わる」ことに気づかない事故が最も多い。
 */
export const replaceContentAssetResponseSchema = z
  .object({ assetId: id, upload: presignedUrlSchema, warning: z.string() })
  .strict();

export const listContentAssetsQuerySchema = cursorQuerySchema.extend({
  folderId: id.optional(),
  tag: z.union([z.string(), z.array(z.string())]).optional(),
  q: z.string().max(200).optional(),
  mime: z.string().max(120).optional(),
  unusedDays: z.coerce.number().int().min(1).max(3650).optional(),
});

export const contentAssetUsageSchema = z
  .object({
    documentId: id,
    documentName: z.string(),
    revision: z.number().int().positive(),
    retentionClass: z.enum(CONTENT_RETENTION_CLASS),
    occurrences: z.number().int().positive(),
    lastSeenAt: isoDateTime,
  })
  .strict();

/* ------------------------------------------------------------- フォルダ */

export const contentAssetFolderSchema = z
  .object({
    id,
    parentId: id.nullable(),
    name: z.string(),
    path: z.string(),
    depth: z.number().int().min(0).max(3),
    assetCount: z.number().int().nonnegative(),
  })
  .strict();

export const createContentAssetFolderSchema = z
  .object({
    parentId: id.nullable().optional(),
    name: z
      .string()
      .min(1)
      .max(60)
      .regex(/^[^/\\]+$/, 'フォルダ名に / と \\ は使えません'),
  })
  .strict();

/* ------------------------------------------------------------ 共有リンク */

export const contentShareLinkSchema = z
  .object({
    id,
    prefix: z.string(),
    revision: z.number().int().positive(),
    label: z.string().nullable(),
    expiresAt: isoDateTime,
    maxViews: z.number().int().positive().nullable(),
    viewCount: z.number().int().nonnegative(),
    lastViewedAt: isoDateTime.nullable(),
    revokedAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
    /**
     * まだ使えるか（失効していない・期限内・閲覧回数が上限未満）。
     * **サーバ側で判定して返す。** 3 つの条件をクライアントごとに
     * 再実装させると、必ずどれかが「期限だけ見て回数を見ない」実装になる。
     */
    active: z.boolean(),
  })
  .strict();

export const createContentShareLinkSchema = z
  .object({
    revision: z.number().int().positive(),
    expiresInDays: z.number().int().min(1).max(CONTENT_SHARE_MAX_DAYS_DEFAULT),
    maxViews: z.number().int().min(1).max(10_000).optional(),
    label: z.string().max(100).optional(),
  })
  .strict();

/** 作成時のみ生トークンを含む応答。以降は `prefix` しか返らない。 */
export const createContentShareLinkResponseSchema = contentShareLinkSchema.extend({
  url: z.string().url(),
  token: z.string(),
});

/* ---------------------------------------------------------- リンクポリシー */

export const contentLinkPolicySchema = z
  .object({
    utmEnabled: z.boolean(),
    utmSource: z.string().max(100),
    utmMedium: z.string().max(100),
    utmCampaignTemplate: z.string().max(200),
    extraParams: z.record(z.string()).default({}),
    /** 既に UTM の付いた URL を上書きしない（既定 false）。 */
    overwriteExisting: z.boolean(),
    excludeHosts: z.array(z.string().max(253)).max(200),
  })
  .strict();

export const updateContentLinkPolicySchema = contentLinkPolicySchema.partial().strict();

/* -------------------------------------------------------------- 予約実行 */

export const contentScheduleSchema = z
  .object({
    id,
    documentId: id,
    revisionId: id,
    action: z.enum(CONTENT_SCHEDULE_ACTION),
    runAt: isoDateTime,
    state: z.enum(CONTENT_SCHEDULE_STATE),
    attempts: z.number().int().nonnegative(),
    executedAt: isoDateTime.nullable(),
    errorMessage: z.string().nullable(),
    createdAt: isoDateTime,
  })
  .strict();

/**
 * 予約の作成。
 *
 * **`action='approve'` は統合面では 403。** 予約承認は「実行時刻に承認が起きる」ので、
 * トークンから作れると `contentApprove` の禁止を時間差で回避できてしまう。
 * 面の判定（`principal.kind === 'org_token'`）を権限チェックより先に行うこと。
 */
export const createContentScheduleSchema = z
  .object({
    revisionId: id,
    action: z.enum(CONTENT_SCHEDULE_ACTION),
    runAt: isoDateTime,
  })
  .strict();

/** 統合面から作れる予約アクション。 */
export const INTEGRATION_ALLOWED_SCHEDULE_ACTIONS = ['archive', 'unarchive'] as const;

export function isScheduleActionAllowedFromIntegration(action: string): boolean {
  return (INTEGRATION_ALLOWED_SCHEDULE_ACTIONS as readonly string[]).includes(action);
}

/* -------------------------------------------------------- スニペット逆引き */

export const snippetDependentSchema = z
  .object({
    documentId: id,
    documentName: z.string(),
    revision: z.number().int().positive(),
    usedSnippetRevision: z.number().int().positive(),
    latestSnippetRevision: z.number().int().positive(),
    outdated: z.boolean(),
  })
  .strict();

/* ------------------------------------------------------------ エクスポート */

export const createContentExportSchema = z
  .object({
    documentIds: z.array(id).max(1000).optional(),
    tags: z.array(z.string()).max(30).optional(),
    from: isoDateTime.optional(),
    to: isoDateTime.optional(),
    format: z.enum(['json', 'html']).default('json'),
  })
  .strict();

export const contentExportStatusSchema = z
  .object({
    jobId: id,
    status: z.string(),
    downloadUrl: z.string().url().nullable(),
    expiresAt: isoDateTime.nullable(),
  })
  .strict();

/* -------------------------------------------------------------- 取り込み */

export const contentImportSchema = z
  .object({
    id,
    sourceKind: z.string(),
    status: z.enum(CONTENT_IMPORT_STATUS),
    totalCount: z.number().int().nonnegative(),
    acceptedCount: z.number().int().nonnegative(),
    rejectedCount: z.number().int().nonnegative(),
    /** **件数と拒否理由の symbol のみ。本文断片を入れない。** */
    report: z.record(z.number().int()).default({}),
    createdDocumentIds: z.array(id).default([]),
    createdAt: isoDateTime,
  })
  .strict();
